import BigNumber from 'bignumber.js';
import { useCallback } from 'react';

import { prepareErrorMessage } from '#/features/wallet/ethereum';
import { logException } from '#/features/logging/logging';
import { useWalletState } from '#/features/wallet/wallet-context';
import { useSystemConfig } from '#/features/system/system-config-context';
import { useStarknetProvider } from '#/features/starknet-wallet/provider';
import { useDebugStarknetTx } from '#/features/starknet';

import { AsyncResult } from '#/utils/types';

import * as WithdrawService from './withdraw-service';

interface InitiateWithdraw {
  (amount: string, asset: string): AsyncResult<void>;
}

export default function useInitiateWithdraw(): InitiateWithdraw {
  const [walletState] = useWalletState();
  const config = useSystemConfig();
  const starknetProvider = useStarknetProvider();
  const debugStarknetTx = useDebugStarknetTx();

  async function initiateWithdraw(
    amount: string,
    asset: string,
  ): AsyncResult<void> {
    if (walletState.ethereumAddress == null) {
      throw new Error(
        'initiateWithdraw called before ethereumAddress available',
      );
    }

    const token = config.getToken(asset);

    try {
      const txHash = await WithdrawService.requestInitiateWithdraw(
        new BigNumber(amount),
        walletState.ethereumAddress,
        token,
        config.paraclear,
        starknetProvider,
      );
      debugStarknetTx('Initiate Withdraw', txHash);
    } catch (err) {
      const description = 'Failed to withdraw funds';
      const { message, isException } = prepareErrorMessage(description, err);
      const error = new Error(description, { cause: err });
      if (isException) logException(error);
      return { ok: false, error, reason: message };
    }

    return { ok: true, data: undefined };
  }

  return useCallback(initiateWithdraw, [
    config,
    debugStarknetTx,
    starknetProvider,
    walletState.ethereumAddress,
  ]);
}
